// TU TECHO — ¿me falta lugar o me falta gente?
//
// Es la pregunta que decide si el dueño invierte en captar más autos o en
// construir. Hoy no la puede contestar: la pantalla vieja le muestra cuántos
// autos entraron, pero nunca cuántos ENTRARÍAN si el lugar estuviera trabajando
// a full. Sin ese denominador, "95 autos por día" no es bueno ni malo.
//
// La cuenta es una sola y es geométrica, no comercial:
//
//     techo = cocheras × horas con autos adentro ÷ permanencia promedio
//
// O sea: cuántas veces se puede reusar cada cochera en un día. Si el auto
// promedio se queda 1 h 36 min y abres 12 h, cada cochera rinde 7,5 autos.
//
// Cuatro decisiones que sostienen el número, y que si alguien "simplifica"
// rompen el bloque entero:
//
// 1. LA PERMANENCIA SE PONDERA POR SALIDAS, no se promedia `stay.avg_hours`.
//    Promediar los promedios diarios le da el mismo peso a un domingo de 4
//    salidas que a un viernes de 300, y el domingo de 4 salidas es justo el día
//    donde un solo auto olvidado adentro dispara el promedio a 9 horas. Con eso
//    la permanencia se infla, el techo se desploma y el bloque le dice al dueño
//    que está al 90% cuando está al 30%.
//
// 2. LO QUE HACE HOY ES LA MEDIANA, NO EL PROMEDIO. La especificación dice
//    "promedio de tickets.total.out", pero es una línea base y la regla de la
//    pantalla es mediana: un solo día de evento corre el promedio hacia arriba y
//    el dueño termina creyendo que su día normal es mejor de lo que es.
//
// 3. LOS DÍAS SIN SALIDAS QUEDAN AFUERA. Un domingo cerrado no describe cómo es
//    un domingo: describe que no se abrió. Contarlo como 0 autos hunde la
//    mediana y agranda artificialmente el hueco contra el techo.
//
// 4. LAS HORAS OPERATIVAS NO SON "hourly > 0". Un solo auto que quedó adentro
//    toda la noche pone las 24 horas en positivo, y entonces la cuenta cree que
//    el estacionamiento abre el doble de lo que abre. El techo se duplica y el
//    error va para el lado peligroso: le dice "te sobra lugar" a alguien que
//    está lleno. Por eso una hora cuenta como operativa recién cuando en un día
//    típico tiene movimiento real (ver `horasOperativas`).
//
// Y una honestidad que va escrita en pantalla, no sólo acá: esto es un TECHO
// TEÓRICO, el número que saldría con todas las cocheras ocupadas todas las
// horas. Nadie llega. Por eso la barra marca el 65%, que es donde un
// estacionamiento ya está trabajando al límite en la práctica.

import React, { useMemo } from "react";
import Bloque from "./Bloque";
import { ORIGEN, TIEMPO, UI } from "./theme";
import { horas, entero, fecha, RUTA_CAPACIDAD } from "./format";
// "Un día tuyo" —cuántos autos mueve la playa en una jornada normal— se calcula
// en `baseline.js` y no acá: el resumen de "Lo que salta a la vista" lo usa para
// decir cuántos autos recibe cada cochera, y ese número tiene que ser divisible
// por el que muestra este bloque. Dos medianas distintas de lo mismo darían dos
// respuestas a "¿cuántos autos hago por día?" en la misma pantalla.
import { MIN_DIA_TIPICO, diaTipico } from "./baseline";

const AZUL = ORIGEN.tickets.color;

// 90 días: suficiente para que la permanencia y el horario se estabilicen, y lo
// bastante corto para que un cambio de temporada no quede promediado con el
// verano pasado.
const VENTANA = 90;

// Menos de un mes con movimiento no alcanza para afirmar cuál es "tu día
// normal", y el techo se compara justamente contra eso. El número vive en
// `baseline.js` porque el resumen deriva de "un día tuyo" y tiene que callarse
// exactamente cuando este bloque se calla.
const MIN_DIAS = MIN_DIA_TIPICO;

// Dónde un estacionamiento ya está lleno en la práctica.
const LIMITE_PRACTICO = 65;

/** Promedia la curva de ocupación de varios días, hora por hora. */
function promediarCurvas(dias) {
  const suma = new Array(24).fill(0);
  let n = 0;
  dias.forEach((d) => {
    const h = d?.occupancy?.hourly;
    if (!Array.isArray(h) || h.length !== 24) return;
    h.forEach((v, i) => { suma[i] += Number(v) || 0; });
    n += 1;
  });
  if (!n) return null;
  return { curva: suma.map((v) => v / n), dias: n };
}

/**
 * Cuántas horas del día el estacionamiento está realmente trabajando.
 *
 * El umbral no es cero (ver nota 4 del encabezado) y tampoco es un número fijo:
 * medio vehículo de piso sirve para una playa de 20 cocheras y es ruido para una
 * de 400, así que se escala con el propio pico del cliente.
 */
function horasOperativas(curva) {
  const pico = Math.max(...curva);
  if (!(pico > 0)) return 0;
  const umbral = Math.max(0.5, pico * 0.05);
  return curva.filter((v) => v >= umbral).length;
}

/**
 * Permanencia promedio ponderada por salidas: horas totales ÷ autos que salieron.
 *
 * `stay.total_hours` es la fuente exacta. `avg_hours × out` es la reconstrucción
 * para los días viejos que no traen el total. Lo que NO se hace nunca es
 * promediar `avg_hours` a secas (nota 1 del encabezado).
 */
function permanenciaPonderada(dias) {
  let horasTotales = 0;
  let salidas = 0;
  dias.forEach((d) => {
    const out = Number(d?.tickets?.total?.out) || 0;
    if (out <= 0) return;
    const total = Number(d?.stay?.total_hours) || 0;
    const avg = Number(d?.stay?.avg_hours) || 0;
    if (total > 0) { horasTotales += total; salidas += out; }
    else if (avg > 0) { horasTotales += avg * out; salidas += out; }
  });
  return salidas > 0 ? { horas: horasTotales / salidas, salidas } : null;
}

/**
 * El techo se redondea a propósito.
 *
 * "301,4 autos por día" es precisión fingida sobre una cuenta que arrastra el
 * error de tres estimaciones. Un número redondo comunica solo que es una
 * aproximación, sin tener que apoyarse en un símbolo que cada uno interpreta
 * distinto.
 */
function redondearTecho(n) {
  if (n >= 200) return Math.round(n / 10) * 10;
  if (n >= 50) return Math.round(n / 5) * 5;
  return Math.round(n);
}

/** "12 h abierto" / "abierto las 24 horas". */
function textoAbierto(h) {
  return h >= 24 ? "abierto las 24 horas" : `${h} h abierto`;
}

/**
 * El veredicto: la respuesta a "¿me falta lugar o me falta gente?".
 *
 * Función pura de un solo número, para poder leerla y discutirla sin abrir el
 * componente. Ningún porcentaje sale a pantalla sin una de estas frases al lado.
 */
function fraseTecho(pct) {
  if (pct >= 95) {
    return {
      titular: `Estás en el límite de lo que da el lugar: el ${pct}% de tu techo`,
      frase: "Si el número pasa del 100% es que la gente rota más rápido de lo que dice la permanencia promedio. De cualquier forma, más autos en el mismo espacio ya no entran.",
    };
  }
  if (pct >= 75) {
    return {
      titular: `Te falta lugar, no gente: estás en el ${pct}% de tu techo`,
      frase: "Crecer de acá en más no depende de conseguir más autos. Lo que queda es la tarifa, la rotación o más cocheras.",
    };
  }
  if (pct >= LIMITE_PRACTICO - 5) {
    return {
      titular: `Estás trabajando cerca del límite: el ${pct}% de tu techo`,
      frase: "Entre 60% y 70% un estacionamiento ya está a full en la práctica. Antes de buscar más volumen, mira si la tarifa está donde tiene que estar.",
    };
  }
  if (pct >= 40) {
    return {
      titular: `Todavía te entran más autos: estás en el ${pct}% de tu techo`,
      frase: "Hay lugar para crecer sin obra. Mira a qué hora se te llena para saber en qué franja entran esos autos.",
    };
  }
  return {
    titular: `Te falta gente, no lugar: estás usando el ${pct}% de tu techo`,
    frase: "El límite no es el espacio. Todo lo que consigas vender en las horas flojas entra sin tocar una sola cochera.",
  };
}

/**
 * Estado vacío: qué falta y cómo se consigue. Nunca una barra en cero.
 *
 * Va por `Bloque` como cualquier otro estado, y el titular dice QUÉ FALTA. Un
 * "Tu techo" arriba de un párrafo gris nombra el tema y no contesta nada: el
 * dueño tiene que leer las tres líneas de abajo para enterarse de que el número
 * no está y de que la pelota, en dos de los cuatro casos, es suya.
 */
const Vacio = ({ titular, texto }) => (
  <Bloque nombre="Tu techo" id="tu-techo" titular={titular} frase={texto} />
);

/** Una fila del detalle: de dónde sale cada ingrediente de la cuenta. */
const Fila = ({ rotulo, valor, nota }) => (
  <div
    className="d-flex flex-column flex-sm-row"
    style={{ padding: "7px 0", borderBottom: `1px solid ${UI.linea}`, gap: 2, alignItems: "baseline" }}
  >
    <div style={{ flex: "0 0 auto", minWidth: 190, fontSize: 12, color: UI.textoSuave, textTransform: "uppercase", letterSpacing: 0.4 }}>
      {rotulo}
    </div>
    <div style={{ flex: "0 0 auto", minWidth: 130, fontSize: 14.5, fontWeight: 700, color: UI.texto }}>
      {valor}
    </div>
    {nota && <div style={{ flex: "1 1 auto", fontSize: 12.5, color: UI.textoSuave, lineHeight: 1.4 }}>{nota}</div>}
  </div>
);

const TuTecho = ({ cerrados = [], vivo = null, capacidad = null }) => {
  // `capacidad` llega por prop para que el bloque no dependa de Redux: index.js
  // ya la resuelve desde `vivo.occupancy.capacity` con el respaldo de la
  // configuración del estacionamiento. Acá sólo se repite el respaldo de `vivo`
  // por si el bloque se monta suelto.
  const cocheras = Number(capacidad) || Number(vivo?.occupancy?.capacity) || 0;

  const c = useMemo(() => {
    const ventana = cerrados.slice(-VENTANA);

    // "Un día tuyo": los días que realmente operaron y la mediana de sus
    // salidas. Sale de `baseline.js` —los que no tuvieron ni una salida no
    // describen un día del negocio (nota 3 del encabezado)— y es el mismo
    // cálculo que usa el resumen.
    const tipico = diaTipico(cerrados, VENTANA);

    // Sobre los dias que OPERARON, no sobre la ventana entera. Los dias en
    // cero diluyen la curva, `horasOperativas` devuelve menos horas, el techo
    // baja y el porcentaje sube — el error va justo para el lado que la nota
    // 3 del encabezado dice evitar: decirle "estas al limite" a alguien que
    // tiene lugar de sobra.
    const curva = promediarCurvas(tipico.operados);

    return {
      diasOperados: tipico.dias,
      desde: tipico.desde,
      hasta: tipico.hasta,
      autos: tipico.autos,
      // El entero que se muestra sale de `baseline`, no de un `Math.round` local:
      // el resumen divide ESE número por las cocheras.
      autosMostrados: tipico.autosMostrados,
      permanencia: permanenciaPonderada(ventana),
      abierto: curva ? horasOperativas(curva.curva) : 0,
      diasConCurva: curva ? curva.dias : 0,
    };
  }, [cerrados]);

  // ── Lo que falta se dice con palabras, en el orden en que el dueño lo puede
  //    resolver: primero lo que configura él, después lo que depende de una
  //    actualización del Desktop.
  if (!cocheras) {
    return (
      <Vacio
        titular="No sé cuántas cocheras tienes, y sin eso no hay techo que calcular"
        texto={`Es el único dato que falta y lo cargas tú, en ${RUTA_CAPACIDAD}. Con ese número, el techo aparece solo.`}
      />
    );
  }
  if (c.diasOperados < MIN_DIAS) {
    return (
      <Vacio
        titular={
          c.diasOperados === 0
            ? "Todavía no hay días con movimiento para calcular tu techo"
            : `Con ${c.diasOperados} ${c.diasOperados === 1 ? "día" : "días"} de operación todavía no sé cuál es tu día normal`
        }
        texto={
          c.diasOperados === 0
            ? "El techo se compara contra un día tuyo habitual, y todavía no hay ninguno. Aparece solo apenas el estacionamiento empiece a operar."
            : `El techo se compara contra tu día habitual, y para saber cuál es hacen falta ${MIN_DIAS} días con movimiento. Con un mes de operación ya se puede.`
        }
      />
    );
  }
  if (!c.permanencia || !(c.permanencia.horas > 0)) {
    return (
      <Vacio
        titular="El tiempo de estadía todavía no llega desde tu PC"
        texto="Sin cuánto se queda cada vehículo no se puede saber cuántas veces se reusa una cochera por día. Empieza a calcularse con la versión 2.0.69 de TicketControl, y si ya actualizaste se completa solo: cada jornada que cierra suma la suya."
      />
    );
  }
  if (!c.abierto) {
    return (
      <Vacio
        titular="Todavía no llegan las horas en las que tienes autos adentro"
        texto="Sin eso no se sabe cuántas horas por día trabaja cada cochera. Aparecen con la próxima actualización de TicketControl, y si ya actualizaste se completan solos: cada jornada que cierra dibuja su curva."
      />
    );
  }

  // ── La cuenta ────────────────────────────────────────────────────────
  const techo = redondearTecho((cocheras * c.abierto) / c.permanencia.horas);
  if (!(techo > 0)) {
    return (
      <Vacio
        titular="Con los datos que llegaron todavía no se puede calcular tu techo"
        texto="Las cocheras, la permanencia y las horas de trabajo están, pero la cuenta no da un número que se pueda mostrar. Se rehace con cada día que cierra: vuelve a mirar en unos días."
      />
    );
  }

  const autos = c.autosMostrados;
  // El porcentaje se calcula contra el techo YA REDONDEADO: es el número que
  // está a la vista, y si alguien hace la división a mano tiene que darle igual.
  const pct = Math.round((autos / techo) * 100);
  const v = fraseTecho(pct);

  const ancho = Math.min(100, Math.max(0, pct));
  const etiquetaAdentro = ancho >= 18;

  return (
    <Bloque
      nombre="Tu techo"
      id="tu-techo"
      titular={v.titular}
      frase={v.frase}
      rango={`${fecha(c.desde)} – ${fecha(c.hasta)} · ${entero(c.diasOperados)} días con movimiento`}
      detalleLabel="Ver cómo sale esta cuenta"
      detalle={
        <div>
          <Fila
            rotulo="Cocheras"
            valor={entero(cocheras)}
            nota="las que tienes cargadas en TicketControl, en los datos del estacionamiento"
          />
          <Fila
            rotulo="Permanencia promedio"
            valor={horas(c.permanencia.horas)}
            nota={`sobre ${entero(c.permanencia.salidas)} salidas, y no promediando el promedio de cada día: un domingo de 4 salidas no puede pesar lo mismo que un viernes de 300`}
          />
          <Fila
            rotulo="Horas trabajando"
            valor={textoAbierto(c.abierto)}
            nota={`horas con autos adentro en la curva de ${entero(c.diasConCurva)} ${c.diasConCurva === 1 ? "día" : "días"}. Un auto que quedó de noche no cuenta como hora abierta`}
          />
          <Fila
            rotulo="Techo estimado"
            valor={`${entero(techo)} autos por día`}
            nota={`${entero(cocheras)} cocheras × ${c.abierto} h ÷ ${horas(c.permanencia.horas)} de permanencia`}
          />
          <Fila
            rotulo="Un día tuyo"
            valor={`${entero(autos)} autos`}
            // Se dice QUE numero es, no COMO se calcula. Que sea la mediana y no el
            // promedio es una decision nuestra para que un dia de evento no corra
            // la vara; el duenno no tiene por que aprender la diferencia para leer
            // su propia pantalla.
            nota={`tu día típico, sobre ${entero(c.diasOperados)} días con movimiento`}
          />
          <p style={{ fontSize: 12.5, color: UI.textoSuave, margin: "12px 0 0", lineHeight: 1.5 }}>
            El techo es el número que saldría con todas las cocheras ocupadas todas las horas que
            abres, así que es un máximo teórico y no una meta. Es una estimación: cambia si cambia tu
            permanencia, tu horario o la cantidad de cocheras.
          </p>
        </div>
      }
    >
      {/* ── La barra ────────────────────────────────────────────────────
          Un solo color: es un solo concepto —autos— y lo único que cambia es la
          magnitud. Pintar el tramo lleno de verde o de rojo según el nivel sería
          meter el banco ESTADO adentro de un gráfico de identidad, y entonces
          estar al 40% se leería como "algo anda mal", que es falso. */}
      <div
        role="img"
        aria-label={`Estás haciendo ${autos} autos por día, el ${pct} por ciento de un techo estimado de ${techo} autos por día. El límite práctico está en el 65 por ciento.`}
      >
        <div style={{ position: "relative", height: 30, background: UI.linea, borderRadius: 15 }}>
          <div
            style={{
              width: `${ancho}%`,
              height: "100%",
              background: AZUL,
              borderRadius: 15,
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              paddingRight: etiquetaAdentro ? 10 : 0,
              transition: "width .4s ease",
            }}
          >
            {etiquetaAdentro && (
              <span style={{ color: "#fff", fontSize: 13, fontWeight: 700 }}>{pct}%</span>
            )}
          </div>

          {/* Cuando la barra es corta la etiqueta no entra adentro y sale afuera.
              Nunca encima del riel: blanco sobre #e5e7eb no se lee. */}
          {!etiquetaAdentro && (
            <span
              style={{
                position: "absolute",
                left: `${ancho}%`,
                top: 0,
                height: "100%",
                display: "flex",
                alignItems: "center",
                marginLeft: 8,
                fontSize: 13,
                fontWeight: 700,
                color: UI.texto,
                whiteSpace: "nowrap",
              }}
            >
              {pct}%
            </span>
          )}

          {/* La marca del 65% va en el gris de TIEMPO: es una referencia, no un
              estado. En rojo se leería como alarma, y estar al 65% no lo es. */}
          <div
            style={{
              position: "absolute",
              left: `${LIMITE_PRACTICO}%`,
              top: -5,
              bottom: -5,
              width: 2,
              background: TIEMPO.anterior.color,
            }}
          />
        </div>

        <div style={{ position: "relative", height: 16, marginTop: 7, fontSize: 11, color: UI.textoSuave }}>
          <span
            style={{
              position: "absolute",
              left: `${LIMITE_PRACTICO}%`,
              transform: "translateX(-50%)",
              whiteSpace: "nowrap",
            }}
          >
            límite práctico
          </span>
          <span style={{ position: "absolute", right: 0, whiteSpace: "nowrap" }}>tu techo</span>
        </div>
      </div>

      {/* ── Los tres renglones ──────────────────────────────────────────
          La barra es la prueba; esto es la lectura. El dueño no tiene por qué
          deducir la cuenta mirando el largo de un rectángulo. */}
      <div style={{ marginTop: 16, fontSize: 14, color: UI.texto, lineHeight: 1.55 }}>
        <p style={{ margin: 0 }}>
          Con {entero(cocheras)} cocheras, {horas(c.permanencia.horas)} de permanencia y{" "}
          {textoAbierto(c.abierto)}, tu estacionamiento da hasta unos {entero(techo)} autos por día.
        </p>
        <p style={{ margin: "6px 0 0" }}>
          Estás haciendo {entero(autos)}: el {pct}% de tu techo.
        </p>
        <p style={{ margin: "6px 0 0", color: UI.textoSuave, fontSize: 13.5 }}>
          Nadie llega al 100%. Entre 60% y 70% ya es un estacionamiento trabajando al límite.
        </p>
        {/* Prohibido el "~": si es estimado se escribe estimado, y se dice de qué
            depende para que el dueño sepa cuánto confiar en el número. */}
        <p style={{ margin: "8px 0 0", fontSize: 11.5, color: UI.textoSuave }}>
          El techo es un cálculo estimado a partir de tus cocheras, tu permanencia y las horas que el
          estacionamiento tiene autos adentro.
        </p>
      </div>
    </Bloque>
  );
};

export default TuTecho;
